/**
 * Schriftgrößen-Store: lässt den Nutzer die Darstellungsgröße der App selbst
 * wählen. Setzt eine Seiten-Skalierung (`--font-scale` → `html { zoom }`) und
 * persistiert die Wahl.
 *
 * „Standard" ist bewusst > 1: die Grundschrift ist gegenüber dem Ur-Zustand
 * um ~1–2 Punkte angehoben (reife, berufliche Zielgruppe → bessere Lesbarkeit).
 * Wer es kleiner mag, wählt „Kompakt"; wer mehr braucht, „Groß"/„Sehr groß".
 */
import { effect, signal } from "@preact/signals";

export type FontScalePref = "kompakt" | "standard" | "gross" | "xl";

const KEY = "sentinus.fontscale";

/** Skalierungsfaktor je Stufe. „standard" = angehobene Grundschrift. */
const SCALES: Record<FontScalePref, number> = {
  // Auftraggeber 2026-06-18: „selbst kleinste Stufe noch zu groß" → Stufen nach
  // unten verschoben. Kompakt ist jetzt deutlich kleiner als die Ur-Größe.
  kompakt: 0.85,
  standard: 1.0, // Default = native Größe
  gross: 1.2,
  xl: 1.4,
};

export const FONT_SCALE_LABELS: Record<FontScalePref, string> = {
  kompakt: "Kompakt",
  standard: "Standard",
  gross: "Groß",
  xl: "Sehr groß",
};

/** Kurzkürzel für die kompakte Anzeige (Design-System: Kürzel statt Wörter). */
export const FONT_SCALE_SHORT: Record<FontScalePref, string> = {
  kompakt: "S",
  standard: "M",
  gross: "L",
  xl: "XL",
};

/** Reihenfolge für Auswahl/Durchschalten (klein → groß). */
export const FONT_SCALE_ORDER: FontScalePref[] = ["kompakt", "standard", "gross", "xl"];

function initial(): FontScalePref {
  const stored = localStorage.getItem(KEY) as FontScalePref | null;
  return stored && stored in SCALES ? stored : "standard";
}

export const fontScalePref = signal<FontScalePref>(initial());

// Anwenden + persistieren, wann immer sich die Wahl ändert.
effect(() => {
  const pref = fontScalePref.value;
  document.documentElement.style.setProperty("--font-scale", String(SCALES[pref]));
  document.documentElement.setAttribute("data-font-scale", pref);
  localStorage.setItem(KEY, pref);
});

export function setFontScale(pref: FontScalePref): void {
  if (pref in SCALES) fontScalePref.value = pref;
}

/** Eine Stufe größer (bis Maximum). */
export function increaseFontScale(): void {
  const i = FONT_SCALE_ORDER.indexOf(fontScalePref.value);
  if (i < FONT_SCALE_ORDER.length - 1) fontScalePref.value = FONT_SCALE_ORDER[i + 1];
}

/** Eine Stufe kleiner (bis Minimum). */
export function decreaseFontScale(): void {
  const i = FONT_SCALE_ORDER.indexOf(fontScalePref.value);
  if (i > 0) fontScalePref.value = FONT_SCALE_ORDER[i - 1];
}
